import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { connect, useDispatch, useSelector } from 'react-redux';
import { translate } from '../../../base/i18n/functions';
import { IconTileView } from '../../../base/icons/svg';
import AbstractButton from '../../../base/toolbox/components/AbstractButton';
import { setParticipantsLayout } from '../../actions';
import { PARTICIPANTS_LAYOUTS } from '../../constants';
import { getParticipantsLayout } from '../../functions';
const MENU_STYLE = `
.deepai-layout-menu{
  position:fixed; z-index:500; min-width:184px; max-height:64vh; overflow-y:auto;
  transform:translateX(-50%);
  background:var(--surface); border:1px solid var(--border); border-radius:14px;
  box-shadow:var(--shadow); padding:6px; font-family:var(--font);
  backdrop-filter:blur(16px) saturate(150%); -webkit-backdrop-filter:blur(16px) saturate(150%);
}
.deepai-layout-menu .li{
  display:flex; align-items:center; gap:9px; width:100%;
  background:transparent; border:0; color:var(--text);
  font-family:var(--font); font-size:13px; font-weight:600; text-align:left;
  padding:9px 10px; border-radius:9px; cursor:pointer; transition:background .12s;
}
.deepai-layout-menu .li:hover{ background:var(--surface-2); }
.deepai-layout-menu .li.sel{ color:var(--accent); }
.deepai-layout-menu .li .dot{ width:9px; height:9px; border-radius:50%; background:var(--border); flex:0 0 auto; }
.deepai-layout-menu .li.sel .dot{ background:var(--accent); box-shadow:0 0 8px var(--glow); }
`;
/**
 * The toolbar trigger button (looks like the old タイル表示 button). Opens the
 * layout menu instead of toggling native tile view.
 */
class LayoutToggleButton extends AbstractButton {
    accessibilityLabel = 'toolbar.participantsLayout';
    icon = IconTileView;
    label = 'toolbar.participantsLayout';
    toggledLabel = 'toolbar.participantsLayout';
    tooltip = 'toolbar.participantsLayout';
    /**
     * Toggled while the menu is open.
     *
     * @returns {boolean}
     */
    _isToggled() {
        return this.props.isOpen;
    }
    /**
     * Opens/closes the menu.
     *
     * @returns {void}
     */
    _handleClick() {
        this.props.onToggle();
    }
}
const ToggleButton = connect()(translate(LayoutToggleButton));
/**
 * Toolbar button that opens a themed menu for choosing the video/participants
 * layout (replaces the old native tile-view button). The menu is portaled to
 * <body> and positioned above the button, so it can't be clipped or hidden by
 * the toolbar/other elements. Selection stays in sync with the Settings dropdown.
 *
 * @param {Object} props - Props injected by the toolbox (buttonKey, customClass).
 * @returns {JSX.Element}
 */
function ParticipantsLayoutButton({ buttonKey, customClass }) {
    const dispatch = useDispatch();
    const layout = useSelector(getParticipantsLayout);
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ bottom: 0, left: 0 });
    const anchorRef = useRef(null);
    const reposition = useCallback(() => {
        const el = anchorRef.current;
        if (!el) {
            return;
        }
        const r = el.getBoundingClientRect();
        setPos({
            bottom: window.innerHeight - r.top + 10,
            left: r.left + (r.width / 2)
        });
    }, []);
    // Position the menu above the button whenever it opens (and on resize).
    useLayoutEffect(() => {
        if (open) {
            reposition();
        }
    }, [open, reposition]);
    useEffect(() => {
        if (!open) {
            return;
        }
        const onDoc = (e) => {
            if (anchorRef.current && !anchorRef.current.contains(e.target)
                && !e.target?.closest?.('.deepai-layout-menu')) {
                setOpen(false);
            }
        };
        const onKey = (e) => e.key === 'Escape' && setOpen(false);
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        window.addEventListener('resize', reposition);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('resize', reposition);
        };
    }, [open, reposition]);
    const onSelect = useCallback((value) => {
        dispatch(setParticipantsLayout(value));
        setOpen(false);
    }, [dispatch]);
    return (React.createElement("div", { className: 'toolbox-button-wth-dialog', ref: anchorRef },
        React.createElement("style", null, MENU_STYLE),
        React.createElement(ToggleButton, { buttonKey: buttonKey, customClass: customClass, isOpen: open, 
            // eslint-disable-next-line react/jsx-no-bind
            onToggle: () => setOpen(o => !o) }),
        open && createPortal(React.createElement("div", { className: 'deepai-layout-menu', role: 'listbox', style: { bottom: `${pos.bottom}px`, left: `${pos.left}px` } }, PARTICIPANTS_LAYOUTS.map(o => (React.createElement("button", { "aria-selected": o.value === layout, className: `li ${o.value === layout ? 'sel' : ''}`, key: o.value, 
            // eslint-disable-next-line react/jsx-no-bind
            onClick: () => onSelect(o.value), role: 'option', type: 'button' },
            React.createElement("span", { className: 'dot' }),
            React.createElement("span", null, o.label))))), document.body)));
}
export default ParticipantsLayoutButton;
